import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../store/hooks';
import { logout } from '../store/slices/authSlice';
import LanguageSwitcher from './LanguageSwitcher';
import NotificationBell from './NotificationBell';

interface NavItem {
    icon: string;
    labelKey: string;
    path: string;
}

const NAV_ITEMS: NavItem[] = [
    { icon: 'fa-solid fa-user', labelKey: 'nav.profile', path: '/profile' },
    { icon: 'fa-solid fa-heart-pulse', labelKey: 'nav.personalTest', path: '/personal-test' },
    { icon: 'fa-solid fa-trophy', labelKey: 'nav.races', path: '/races' },
    { icon: 'fa-solid fa-table-list', labelKey: 'nav.trainingSchedule', path: '/' },
    { icon: 'fa-solid fa-dumbbell', labelKey: 'nav.trainingLibrary', path: '/training-library' },
];

interface SidebarProps {
    /** Mobile drawer open state. Ignored at md+, where the sidebar is always visible. */
    isOpen?: boolean;
    onClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen = false, onClose }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { t } = useTranslation();

    const handleLogout = () => {
        dispatch(logout());
        navigate('/login');
    };

    return (
        <aside
            className={`w-72 bg-slate-800 border-r border-slate-700 flex-shrink-0 flex flex-col text-slate-300 h-screen fixed left-0 top-0 z-[60] md:z-40 transition-transform duration-200 md:transition-none ${isOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}
            style={{
                backgroundImage: 'radial-gradient(#334155 1px, transparent 1px)',
                backgroundSize: '20px 20px'
            }}
        >
            {/* Logo */}
            <div className="h-24 flex items-center px-5 border-b border-slate-700 bg-slate-900/50">
                <div className="flex justify-start w-full">
                    <img src="/logo.png" alt="Enjoy Triathlon" className="w-44 h-auto object-contain" />
                </div>
            </div>

            {/* Navigation */}
            <nav className="flex-1 overflow-y-auto py-8 px-4 space-y-2">
                {NAV_ITEMS.map((item) => {
                    const isActive = location.pathname === item.path;

                    return (
                        <Link
                            key={item.path}
                            to={item.path}
                            onClick={onClose}
                            className={`flex items-center gap-4 px-4 py-3 rounded-xl transition-all group ${isActive
                                ? 'bg-gradient-to-r from-cyan-500/20 to-transparent border-l-4 border-cyan-500 text-white shadow-lg shadow-black/20'
                                : 'hover:bg-slate-700/50 hover:text-white'
                                }`}
                        >
                            <div className="w-8 flex justify-center">
                                <i className={`${item.icon} text-lg ${isActive ? 'text-cyan-500' : 'text-slate-400 group-hover:text-cyan-500'
                                    } transition-colors`}></i>
                            </div>
                            <span className={`font-${isActive ? 'semibold' : 'medium'}`}>{t(item.labelKey)}</span>
                        </Link>
                    );
                })}
            </nav>

            {/* Notifications */}
            <NotificationBell />

            {/* Language + Logout */}
            <div className="border-t border-slate-700">
                <LanguageSwitcher />
                <div className="px-4 pb-4">
                    <button
                        onClick={handleLogout}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-red-400 hover:text-red-300 rounded-lg transition-colors font-medium text-sm"
                    >
                        <i className="fa-solid fa-right-from-bracket"></i>
                        <span>{t('nav.logout')}</span>
                    </button>
                </div>
            </div>
        </aside>
    );
};

export default Sidebar;
